/**
 * Utilitaires pour la gestion de la fusion d'entités (auteurs, éditeurs, etc.)
 */

/**
 * Formate le nom complet d'une entité
 * @param {Object} entity - L'entité (auteur, éditeur, etc.)
 * @param {string} entity.nom - Le nom
 * @param {string} entity.prenom - Le prénom (optionnel)
 * @returns {string} Le nom formaté
 */
export const formatEntityName = (entity) => {
  if (!entity) return 'Entité inconnue';
  
  // Pour les auteurs avec prénom
  if (entity.prenom !== undefined) {
    return `${entity.prenom || ''} ${entity.nom || ''}`.trim();
  }
  
  // Pour les éditeurs (juste le nom)
  return entity.nom || 'Sans nom';
};

/**
 * Retourne la couleur du chip de similarité
 * @param {number} similarity - Le score de similarité (0-1)
 * @returns {string} La couleur du chip Material-UI
 */
export const getSimilarityColor = (similarity) => {
  if (similarity >= 0.9) return 'success';
  if (similarity >= 0.8) return 'warning';
  return 'error';
};

/**
 * Génère les paramètres de configuration pour la détection de doublons
 * @returns {Array} Les marqueurs pour le slider de seuil
 */
export const getSimilarityThresholdMarks = () => [
  { value: 0.5, label: '50%' },
  { value: 0.7, label: '70%' },
  { value: 0.8, label: '80%' },
  { value: 0.9, label: '90%' },
  { value: 1.0, label: '100%' }
];

/**
 * Configuration pour les différents types d'entités
 */
export const entityConfigs = {
  auteurs: {
    singular: 'auteur',
    plural: 'auteurs',
    icon: 'Person',
    apiKey: 'auteursAPI',
    nameField: 'nom',
    hasPrenom: true,
    countField: 'nombre_livres',
    countLabel: 'livre(s)',
    additionalFields: ['date_naissance'],
    formatAdditionalInfo: (entity) => {
      const info = [];
      if (entity.date_naissance) {
        info.push(`Né(e) en ${entity.date_naissance}`);
      }
      return info;
    }
  },
  editeurs: {
    singular: 'éditeur',
    plural: 'éditeurs',
    icon: 'Business',
    apiKey: 'editeursAPI',
    nameField: 'nom',
    hasPrenom: false,
    countField: 'nombre_livres',
    countLabel: 'livre(s)',
    additionalFields: ['adresse', 'site_web'],
    formatAdditionalInfo: (entity) => {
      const info = [];
      if (entity.adresse) {
        info.push(entity.adresse);
      }
      if (entity.site_web) {
        info.push(entity.site_web);
      }
      return info;
    }
  }
};

/**
 * Génère les options pour le select de l'entité principale lors de la fusion
 * @param {Array} group - Le groupe d'entités similaires
 * @param {Object} config - La configuration de l'entité
 * @returns {Array} Les options formatées
 */
export const generateMergeOptions = (group, config) => {
  if (!group || !config) return [];
  
  return group
    .filter(entity => entity)
    .map(entity => ({
      id: entity.id,
      label: `${formatEntityName(entity)} (${entity[config.countField] || 0} ${config.countLabel})`,
      entity
    }));
};

/**
 * Valide les paramètres de fusion
 * @param {number} targetId - L'ID de l'entité cible
 * @param {Array} group - Le groupe d'entités à fusionner
 * @returns {Object} Résultat de validation
 */
export const validateMergeParams = (targetId, group) => {
  if (!targetId) {
    return {
      isValid: false,
      error: 'Veuillez sélectionner l\'entité principale'
    };
  }
  
  if (!group || group.length < 2) {
    return {
      isValid: false,
      error: 'Groupe de fusion invalide'
    };
  }
  
  const targetExists = group.some(entity => entity && entity.id === targetId);
  if (!targetExists) {
    return {
      isValid: false,
      error: 'L\'entité cible n\'existe pas dans le groupe'
    };
  }
  
  return { isValid: true };
};

/**
 * Prépare les données pour l'API de fusion
 * @param {number} targetId - L'ID de l'entité cible
 * @param {Array} group - Le groupe d'entités à fusionner
 * @returns {Object} Les données formatées pour l'API
 */
export const prepareMergeData = (targetId, group) => {
  const sourceIds = group
    .filter(entity => entity && entity.id !== targetId)
    .map(entity => entity.id);
    
  return { targetId, sourceIds };
};

/**
 * Génère les messages de succès/erreur pour les opérations de fusion
 * @param {string} entityType - Le type d'entité (auteurs, editeurs)
 * @param {string} operation - L'opération (merge, delete)
 * @param {boolean} isSuccess - Si l'opération a réussi
 * @returns {string} Le message formaté
 */
export const getMergeMessage = (entityType, operation, isSuccess) => {
  const config = entityConfigs[entityType];
  if (!config) return 'Opération terminée';
  
  const { singular, plural } = config;
  
  if (operation === 'merge') {
    return isSuccess 
      ? `${plural.charAt(0).toUpperCase() + plural.slice(1)} fusionnés avec succès`
      : `Erreur lors de la fusion des ${plural}`;
  }
  
  if (operation === 'delete') {
    return isSuccess
      ? `${singular.charAt(0).toUpperCase() + singular.slice(1)} supprimé avec succès`
      : `Erreur lors de la suppression de l'${singular}`;
  }
  
  return 'Opération terminée';
};

/**
 * Génère les clés de cache React Query pour invalider
 * @param {string} entityType - Le type d'entité
 * @returns {Array} Les clés de cache à invalider
 */
export const getCacheKeys = (entityType) => {
  const baseKey = entityType.replace(/s$/, ''); // Remove trailing 's'
  return [
    `${baseKey}-duplicates`,
    `${baseKey}-list`,
    `${entityType}`,
    'stats' // Invalider aussi les stats globales
  ];
};
